import React from 'react'
import { Accordion } from '@sainsburys-tech/bolt'
import noop from 'lodash.noop'
import { NutritionTombstones, extractTombstoneNutritionInfo } from './components/NutritionTombstones'
import NutritionTable from '../NutritionTable/src/NutritionTable'
import { pushEventAndTrack } from '../../helpers/taggingSerializer'
import { scrollToAccordion } from '../../helpers/openAccordion'
import { Heading } from '../Description/Description.styles'
import * as Styled from './NutritionSection.styles'

const extractNutritionInfo = nutrition => {
  if (!nutrition.nutritionInformation || !nutrition.nutritionInformation.nutritionData) {
    return []
  }
  return nutrition.nutritionInformation.nutritionData.map(data => {
    const rowData = data.nutritionTable.nutritionData
    const columnHeaders = data.nutritionTable.columnHeaders
    const tombstones = columnHeaders.length > 2 ? extractTombstoneNutritionInfo(data) : []
    const energyIndex = rowData.findIndex(item => item.nutrientName.includes('Energy'))
    const energyText =
      energyIndex !== -1 &&
      `Typical Values ${rowData[energyIndex].servingData[0].servingTitle} : Energy ${
        rowData[energyIndex].servingData[0]?.servingValue
      } ${rowData[energyIndex].servingData[1]?.servingValue || ''}`
    const typicalValueAmount = data.nutritionTable.columnHeaders[1]
    const itemName = data.name
    return {
      itemName,
      tombstones,
      energyText,
      typicalValueAmount,
      columnHeaders,
      rowData
    }
  })
}

const getIngredientHtml = nutrition => {
  return (
    nutrition.ingredientInformation?.ingredients?.map(item => ({
      name: item.name,
      html: { __html: item.htmlIngredients }
    })) || []
  )
}

const dataTest = 'nutrition-accordion'

const NutritionSection = nutrition => {
  const ingredients = getIngredientHtml(nutrition)
  const data = extractNutritionInfo(nutrition)
  const onAccordionInteraction = (index, show) => {
    nutrition.onAccordionChange('nutrition', show)
    if (show) scrollToAccordion('#nutritional-info')

    // Adobe Analytics Tracking Code
    const adobeAnalytics = show ? pushEventAndTrack : noop
    adobeAnalytics({
      eventInfo: {
        eventName: 'pdp_showMore',
        eventAction: 'Nutritional Information'
      }
    })
  }
  const NutritionHeader = <Styled.Heading> Nutritional Information </Styled.Heading>
  const accordionOptions = {
    title: NutritionHeader,
    onClick: onAccordionInteraction,
    name: 'nutrition',
    id: 'nutritional-info',
    'data-test': dataTest,
    index: dataTest
  }

  return (
    <Accordion.Item as='h3' hasBorderBottom noAnimation isOpen {...accordionOptions}>
      <>
        {data.length > 0 &&
          data.map((item, index) => (
            <React.Fragment key={index}>
              {item.rowData && (
                <NutritionTable
                  key={`${dataTest}-table-${index}`}
                  headers={item.columnHeaders}
                  content={item.rowData}
                  alignCaption='left'
                  captionBackground=''
                  caption={data.length > 1 && item.itemName}
                  data-test={`${dataTest}-table-${index}`}
                />
              )}
              {item.tombstones.length > 0 &&
                data.map((itemT, indexT) => (
                  <Styled.Container
                    key={`${dataTest}-tombstones-${indexT}`}
                    data-test={`${dataTest}-tombstones-${indexT}`}
                  >
                    {itemT.typicalValueAmount && (
                      <Styled.SubHeading> {itemT.typicalValueAmount} Typical Values</Styled.SubHeading>
                    )}
                    <NutritionTombstones
                      data-test={`${dataTest}-tombstones-${indexT}-list`}
                      tombstones={itemT.tombstones}
                    />
                    <Styled.TombstoneSubText>
                      {itemT.energyText && <p>{itemT.energyText}</p>}
                      <p> % Reference intake of an average adult (8400 kJ/2000 kcal)</p>
                    </Styled.TombstoneSubText>
                  </Styled.Container>
                ))}
            </React.Fragment>
          ))}
        {ingredients.length > 0 &&
          ingredients.map((items, index) => (
            <Styled.Container key={`${dataTest}-ingredients-${index}`} data-test={`${dataTest}-ingredients-${index}`}>
              <Heading>Ingredients {data.length > 1 && `- ${items.name}`}</Heading>
              <p>
                <strong>INGREDIENTS: </strong>
                <span data-test={`${dataTest}-ingredients-${index}-ingredient`} dangerouslySetInnerHTML={items.html} />
              </p>
            </Styled.Container>
          ))}
      </>
    </Accordion.Item>
  )
}

export default NutritionSection
